import React, { useEffect, useState } from "react";
import { CheckIcon, LogOutIcon, AlertTriangleIcon } from "lucide-react";
import gbLogo from "../../../assets/images/gb-logo-navy.png";
import { SegTabs } from "./SegTabs";
import "./onboarding.css";

/**
 * The onboarding page's frame and layout (GB-redesign-spec/onboarding-vera-mock,
 * 2026-10-12, approved with Ari's three changes): the slim top bar, the two
 * panes (Vera about 2/3 on the left, one side panel on the right; on a phone,
 * Vera full width with Chat / Progress tabs), the side panel's titled
 * sections, the "You are here" card and the help row. Styling: onboarding.css.
 */

export type SaveState = "idle" | "saving" | "saved" | "error";

/** "Saved" in the bar: only what really happened in this session, never a standing claim. */
export function SaveStatus({ state, at }: { state: SaveState; at?: number | null }): React.ReactElement | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (state !== "saved") return;
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, [state]);
  if (state === "idle") return null;
  if (state === "saving") return <span className="ds-slimbar-st" role="status">Saving</span>;
  if (state === "error") {
    return (
      <span className="ds-slimbar-st" data-st="error" role="status">
        <AlertTriangleIcon aria-hidden="true" strokeWidth={1.75} />
        Not saved
      </span>
    );
  }
  const when = at == null ? "" : now - at < 60_000 ? "just now" : `at ${new Date(at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
  return (
    <span className="ds-slimbar-st" role="status">
      <CheckIcon aria-hidden="true" strokeWidth={2} />
      Saved
      {when && <span>&nbsp;{when}</span>}
    </span>
  );
}

/** The bar across the top of a frame with no navigation (mock `.bar`): logo, name, status, sign out. */
export function SlimBar({
  title,
  subtitle,
  status,
  onSignOut,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  status?: React.ReactNode;
  onSignOut?: () => void;
}): React.ReactElement {
  return (
    <header className="ds-slimbar">
      <img className="ds-slimbar-logo" src={gbLogo} alt="GlobalBridge" />
      <span className="ds-slimbar-sep" aria-hidden="true" />
      <div className="ds-slimbar-t">
        {title}
        {subtitle != null && <small>{subtitle}</small>}
      </div>
      <span className="ds-slimbar-sp" aria-hidden="true" />
      {status}
      {onSignOut && (
        <button type="button" className="ds-slimbar-out" onClick={onSignOut} aria-label="Sign out">
          <LogOutIcon aria-hidden="true" strokeWidth={1.75} />
          <span>Sign out</span>
        </button>
      )}
    </header>
  );
}

export type TwoPaneSide = "main" | "side";

export interface TwoPaneProps {
  main: React.ReactNode;
  side: React.ReactNode;
  /** Accessible names of the two regions. */
  mainLabel: string;
  sideLabel: string;
  /** The phone tabs' labels (a label may carry a count). */
  tabs: Record<TwoPaneSide, React.ReactNode>;
  /** Controlled phone pane. Omitted, the component keeps its own (starting on `main`). */
  pane?: TwoPaneSide;
  onPaneChange?: (pane: TwoPaneSide) => void;
}

/**
 * Two panes (mock `.panes`): the main pane about 2/3, the side panel 1/3 and
 * never under 380px; under 1180px the panel keeps 380px. On a phone one pane
 * at a time behind two tabs. Each pane scrolls on its own.
 */
export function TwoPane({ main, side, mainLabel, sideLabel, tabs, pane, onPaneChange }: TwoPaneProps): React.ReactElement {
  const [own, setOwn] = useState<TwoPaneSide>("main");
  const shown = pane ?? own;
  const choose = onPaneChange ?? setOwn;
  return (
    <div className="ds-2p" data-pane={shown}>
      <div className="ds-2p-tabs">
        <SegTabs<TwoPaneSide>
          role="tabs"
          aria-label="Page sections"
          value={shown}
          onChange={choose}
          options={[
            { value: "main", label: tabs.main },
            { value: "side", label: tabs.side },
          ]}
        />
      </div>
      <section className="ds-2p-main" aria-label={mainLabel}>
        {main}
      </section>
      <aside className="ds-2p-side" aria-label={sideLabel}>
        {side}
      </aside>
    </div>
  );
}

/** A count inside a tab label (mock `.tabs .cnt`). */
export const TabCount: React.FC<{ n: number; label: string }> = ({ n, label }) => (
  <span className="ds-2p-cnt num" aria-label={label}>
    {n}
  </span>
);

/** A titled section of the side panel (mock `.sec-t` + its card). */
export function PanelSection({
  title,
  right,
  id,
  children,
}: {
  title: string;
  right?: React.ReactNode;
  id?: string;
  children: React.ReactNode;
}): React.ReactElement {
  const headingId = id ? `${id}-h` : undefined;
  return (
    <section className="ds-sec" id={id} aria-labelledby={headingId} aria-label={headingId ? undefined : title}>
      <div className="ds-sec-t">
        <h2 id={headingId}>{title}</h2>
        {right != null && <span>{right}</span>}
      </div>
      {children}
    </section>
  );
}

/** "You are here" (mock `.now`): the current step and its next action. */
export function NowCard({
  eyebrow,
  title,
  children,
  actions,
}: {
  eyebrow: React.ReactNode;
  title: React.ReactNode;
  children?: React.ReactNode;
  actions?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="ds-card ds-now">
      <div className="ds-now-eb">{eyebrow}</div>
      <h3>{title}</h3>
      {children != null && <p className="ds-now-p">{children}</p>}
      {actions != null && <div className="ds-now-acts">{actions}</div>}
    </div>
  );
}

/** A plain note inside a panel card (mock `.empty`). */
export const PanelNote: React.FC<{ children: React.ReactNode }> = ({ children }) => <p className="ds-pnote">{children}</p>;

/** The "talk to a person" row (mock `.help`), with an optional note under it. */
export function HelpRow({
  children,
  action,
  note,
}: {
  children: React.ReactNode;
  action: React.ReactNode;
  note?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="ds-card ds-help">
      <span>{children}</span>
      {action}
      {note != null && <span className="ds-help-note">{note}</span>}
    </div>
  );
}
